/**
 * How each kind of stat card turns its payload keys into what the card shows.
 *
 * The descriptor says what a card *is* — a bare count, a figure against a
 * comparison, a value with a series behind it — through its `type`, and names
 * the payload keys that type reads. A renderer is the one place that knows
 * how to read those keys and what the card should display; a new kind of card
 * is a new entry here plus its backend type, with nothing else touched.
 *
 * A type the registry does not know renders as a count. A backend a version
 * ahead of this bundle can ship a new card type and its number still appears,
 * which is the same forward-compatibility contract the descriptor columns
 * hold — see normalizeDescriptorColumn.
 *
 * Contains:
 * - STAT_CARD_TYPES            — the types this bundle draws
 * - DEFAULT_STAT_CARD_TYPE     — what an unknown type falls back to
 * - BASE_STAT_CARD_RENDERERS   — seed map of type → renderer
 * - resolveStatCardRenderer    — look a type up in a registry, count for unknowns
 *
 * Returns plain JS — no JSX, no redux.
 */

import { resolveStatCardValue } from './normalizeDescriptorStatCard';

/** The type a card renders as when the descriptor's is not one this bundle draws. */
export const DEFAULT_STAT_CARD_TYPE = 'count';

/** Percentage change of *value* against *previous*, in StatCard's convention. */
function percentChange(value, previous) {
  if (previous === 0) return value === 0 ? 0 : Infinity;
  return ((value - previous) / previous) * 100;
}

/**
 * A bare number — the shape every card had before the type existed.
 *
 * @param {object} card - Normalized descriptor card.
 * @param {?object} stats - The `/stats/` payload, or null before it lands.
 * @returns {{ value: number }}
 */
function renderCount(card, stats) {
  return { value: resolveStatCardValue(stats, card.key) };
}

/**
 * A number and how it moved against the comparison figure the card names.
 * The comparison key is redacted alongside the primary one for a card the
 * tenant cannot see, so a missing comparison reads as no movement rather than
 * as a drop to zero.
 *
 * @param {object} card - Normalized descriptor card; `comparisonKey` names the figure.
 * @param {?object} stats
 * @returns {{ value: number, previousValue: number, change: number }}
 */
function renderDelta(card, stats) {
  const value = resolveStatCardValue(stats, card.key);
  const previousValue = resolveStatCardValue(stats, card.comparisonKey);
  return { value, previousValue, change: percentChange(value, previousValue) };
}

/**
 * A value whose series this bundle does not draw yet. Registered so the type
 * is known rather than falling through the unknown-type path, and so adding
 * the chart later changes this function alone.
 *
 * @param {object} card
 * @param {?object} stats
 * @returns {{ value: number }}
 */
function renderSparkline(card, stats) {
  return renderCount(card, stats);
}

/** @type {Record<string, (card: object, stats: ?object) => object>} */
export const BASE_STAT_CARD_RENDERERS = Object.freeze({
  count: renderCount,
  delta: renderDelta,
  sparkline: renderSparkline,
});

/** The card types this bundle draws. */
export const STAT_CARD_TYPES = Object.freeze(Object.keys(BASE_STAT_CARD_RENDERERS));

/**
 * Returns the renderer registered under `type`, falling back to the count
 * renderer for a type the registry has no entry for.
 *
 * @param {string} type - Card type from the backend descriptor.
 * @param {Record<string, Function>} [registry] - Registry to look up.
 *   Defaults to BASE_STAT_CARD_RENDERERS.
 * @returns {(card: object, stats: ?object) => object}
 */
export function resolveStatCardRenderer(type, registry = BASE_STAT_CARD_RENDERERS) {
  const known = registry ?? BASE_STAT_CARD_RENDERERS;
  return known[type] ?? known[DEFAULT_STAT_CARD_TYPE] ?? renderCount;
}
